import { z } from "zod";

export const userRegistrationTypes = [
  "client_owner",
  "vendor_owner",
  "independent_specialist",
  "invited_member",
  "platform_staff",
] as const;

export const publicRegistrationTypes = [
  "client_owner",
  "vendor_owner",
  "independent_specialist",
] as const;

export const userRegistrationTypeSchema = z.enum(userRegistrationTypes);
export const publicRegistrationTypeSchema = z.enum(publicRegistrationTypes);
export type UserRegistrationType = z.infer<typeof userRegistrationTypeSchema>;
export type PublicRegistrationType = z.infer<typeof publicRegistrationTypeSchema>;

export const userStatusSchema = z.enum([
  "pending_verification",
  "active",
  "blocked",
  "archived",
]);

function isValidTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

export const normalizedEmailSchema = z
  .string()
  .trim()
  .email()
  .max(320)
  .transform((value) => value.toLowerCase());

export const newPasswordSchema = z
  .string()
  .min(10)
  .max(128)
  .regex(/[A-Za-zА-Яа-я]/u, "Password must contain a letter")
  .regex(/[0-9]/, "Password must contain a number");

const personNameSchema = z.string().trim().min(2).max(100);
const oneTimeTokenSchema = z.string().trim().min(32).max(256);

export const registrationRequestSchema = z.object({
  registrationType: publicRegistrationTypeSchema,
  firstName: personNameSchema,
  lastName: personNameSchema,
  email: normalizedEmailSchema,
  phone: z
    .string()
    .trim()
    .min(7)
    .max(32)
    .regex(/^\+?[0-9 ()-]+$/)
    .optional(),
  timezone: z.string().trim().min(1).max(64).refine(isValidTimeZone, "Invalid IANA timezone"),
  password: newPasswordSchema,
  termsAccepted: z.literal(true),
  privacyAccepted: z.literal(true),
  personalDataAccepted: z.literal(true),
  marketingAccepted: z.boolean().default(false),
});

export type RegistrationRequest = z.infer<typeof registrationRequestSchema>;

export const registeredUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  registrationType: publicRegistrationTypeSchema,
  status: z.literal("pending_verification"),
});

export const registrationResponseSchema = z.object({
  user: registeredUserSchema,
  verificationRequired: z.literal(true),
});

export type RegistrationResponse = z.infer<typeof registrationResponseSchema>;

export const loginRequestSchema = z.object({
  email: normalizedEmailSchema,
  password: z.string().min(1).max(128),
});

export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const authenticatedUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  registrationType: userRegistrationTypeSchema,
  status: z.literal("active"),
  emailVerified: z.literal(true),
});

export type AuthenticatedUser = z.infer<typeof authenticatedUserSchema>;

export const loginResponseSchema = z.object({
  user: authenticatedUserSchema,
  session: z.object({
    expiresAt: z.string().datetime(),
  }),
});

export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const currentSessionResponseSchema = loginResponseSchema;
export type CurrentSessionResponse = z.infer<typeof currentSessionResponseSchema>;

export const emailVerificationRequestSchema = z.object({
  email: normalizedEmailSchema,
});

export const emailVerificationRequestResponseSchema = z.object({
  accepted: z.literal(true),
});

export const emailVerificationConfirmSchema = z.object({
  token: oneTimeTokenSchema,
});

export const emailVerificationConfirmResponseSchema = z.object({
  verified: z.literal(true),
});

export type EmailVerificationRequest = z.infer<typeof emailVerificationRequestSchema>;
export type EmailVerificationConfirm = z.infer<typeof emailVerificationConfirmSchema>;

export const passwordResetRequestSchema = z.object({
  email: normalizedEmailSchema,
});

export const passwordResetRequestResponseSchema = z.object({
  accepted: z.literal(true),
});

export const passwordResetConfirmSchema = z.object({
  token: oneTimeTokenSchema,
  newPassword: newPasswordSchema,
});

export const passwordResetConfirmResponseSchema = z.object({
  changed: z.literal(true),
});

export const passwordChangeSchema = z.object({
  currentPassword: z.string().min(1).max(128),
  newPassword: newPasswordSchema,
});

export const passwordChangeResponseSchema = z.object({
  changed: z.literal(true),
});

export type PasswordResetRequest = z.infer<typeof passwordResetRequestSchema>;
export type PasswordResetConfirm = z.infer<typeof passwordResetConfirmSchema>;
export type PasswordChange = z.infer<typeof passwordChangeSchema>;
